import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { CampaignEngagementItem } from "../../../api/campaigns";
import {
  ENGAGEMENT_STATE_COLOR,
  updateCampaignEngagement,
  fetchEngagementTargets,
  fetchEngagementActiveCall,
  haltCampaignEngagement,
} from "../../../api/campaigns";
import { buildLiveKitMeetUrl } from "../../../api/dispatch";
import { ApiError } from "../../../lib/api";
import { useIsMobile } from "../../../lib/useMediaQuery";
import { ScrollableTable } from "../../../components/ScrollableTable";
import { Button } from "../../../components/ui/Button";

interface Props {
  campaignId: string;
  engagement: CampaignEngagementItem;
  editable: boolean;
  onClose: () => void;
}

// Convert ISO string → datetime-local value (matches AuthCard pattern in Detail.tsx)
function isoToDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

const waveHeights = [40, 70, 90, 50, 80, 60, 95, 45, 75, 85, 55, 90, 65, 78, 50];
const waveDelays = [0, 0.1, 0.2, 0.3, 0.05, 0.15, 0.25, 0.35, 0.12, 0.22, 0.32, 0.08, 0.18, 0.28, 0.38];

const fieldLabelStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 9.5,
  textTransform: "uppercase",
  letterSpacing: "0.14em",
  color: "var(--text-muted)",
  display: "block",
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: 12,
  padding: "7px 10px",
  background: "var(--bg-input)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-input)",
  color: "var(--text-primary)",
  width: "100%",
  boxSizing: "border-box",
  letterSpacing: "0.02em",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
};

export function EngagementPanel({ campaignId, engagement: eng, editable, onClose }: Props) {
  const qc = useQueryClient();
  const isMobile = useIsMobile();

  // ── schedule state ──
  const [schedStart, setSchedStart] = useState(isoToDatetimeLocal(eng.scheduled_start_at));
  const [schedEnd, setSchedEnd] = useState(isoToDatetimeLocal(eng.scheduled_end_at));
  const [dailyStart, setDailyStart] = useState(eng.active_daily_start ?? "09:00");
  const [dailyEnd, setDailyEnd] = useState(eng.active_daily_end ?? "18:00");
  const [staggerPattern, setStaggerPattern] = useState(eng.stagger_pattern);
  const [staggerInterval, setStaggerInterval] = useState(eng.stagger_interval_seconds ?? 60);
  const [staggerJitter, setStaggerJitter] = useState(eng.stagger_jitter_seconds);

  const [saveError, setSaveError] = useState<string | null>(null);

  // ── save mutation ──
  const saveMut = useMutation({
    mutationFn: () =>
      updateCampaignEngagement(campaignId, eng.sort_order, {
        ...(schedStart ? { scheduled_start_at: new Date(schedStart).toISOString() } : {}),
        ...(schedEnd ? { scheduled_end_at: new Date(schedEnd).toISOString() } : {}),
        active_daily_start: dailyStart,
        active_daily_end: dailyEnd,
        stagger_pattern: staggerPattern,
        ...(staggerPattern === "fixed_interval" ? { stagger_interval_seconds: staggerInterval } : {}),
        stagger_jitter_seconds: staggerJitter,
      }),
    onSuccess: () => {
      setSaveError(null);
      qc.invalidateQueries({ queryKey: ["campaign-engagements", campaignId] });
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setSaveError(err.detail);
      } else {
        setSaveError("Failed to save");
      }
    },
  });

  // ── targets query ──
  const { data: targetsData } = useQuery({
    queryKey: ["engagement-targets", campaignId, eng.sort_order],
    queryFn: () => fetchEngagementTargets(campaignId, eng.sort_order),
    refetchInterval: eng.state === "running" ? 3000 : false,
  });
  const targets = targetsData?.items ?? [];

  // ── halt mutation ──
  const haltMut = useMutation({
    mutationFn: () => haltCampaignEngagement(campaignId, eng.sort_order),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["campaign-engagements", campaignId] });
    },
  });

  // ── listen in ──
  const handleListenIn = async () => {
    try {
      const r = await fetchEngagementActiveCall(campaignId, eng.sort_order);
      window.open(
        buildLiveKitMeetUrl(r.livekit_url, r.operator_access_token),
        "_blank",
        "noopener,noreferrer",
      );
    } catch {
      alert("No active call found for this engagement.");
    }
  };

  const stateColor = ENGAGEMENT_STATE_COLOR[eng.state] ?? "var(--text-muted)";
  const isRunning = eng.state === "running";
  const hasActiveCall = isRunning && !!eng.active_call_id;

  return (
    <>
      {/* waveform @keyframes injected inline — scoped to this component render */}
      <style>{`
        @keyframes wave {
          0%, 100% { height: 30%; }
          50% { height: 100%; }
        }
        @keyframes ring-expand {
          0% { opacity: 0.7; transform: scale(1); }
          100% { opacity: 0; transform: scale(2.5); }
        }
      `}</style>

      {/* scrim */}
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15,23,42,0.28)",
          zIndex: 50,
        }}
      />

      {/* panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: isMobile ? "100vw" : 520,
          maxWidth: isMobile ? "100vw" : "90vw",
          left: isMobile ? 0 : "auto",
          height: isMobile ? "100dvh" : undefined,
          background: "var(--bg-card)",
          borderLeft: isMobile ? "none" : "1px solid var(--text-primary)",
          zIndex: 60,
          overflowY: "auto",
          boxShadow: isMobile ? "none" : "-20px 0 40px -20px rgba(15,23,42,0.35)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* ── header ── */}
        <div
          style={{
            padding: "18px 24px",
            borderBottom: "1px solid var(--border)",
            background: "var(--bg-input)",
            position: "sticky",
            top: 0,
            zIndex: 5,
          }}
        >
          <button
            onClick={onClose}
            style={{
              float: "right",
              background: "transparent",
              border: "1px solid var(--border-strong)",
              borderRadius: "var(--radius-input)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              padding: "4px 10px",
              cursor: "pointer",
              color: "var(--text-secondary)",
              letterSpacing: "0.1em",
            }}
          >
            Close ✕
          </button>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "var(--text-muted)",
              marginBottom: 6,
            }}
          >
            #{String(eng.sort_order + 1).padStart(2, "0")} / Engagement
          </div>
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 22,
              fontWeight: 500,
              margin: "0 0 10px",
              letterSpacing: "-0.018em",
              lineHeight: 1.15,
              color: "var(--text-primary)",
            }}
          >
            {eng.engagement_display_name}
          </h2>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 9px",
              border: `1px solid ${stateColor}`,
              fontFamily: "var(--font-mono)",
              fontSize: 9.5,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: stateColor,
              background: `color-mix(in srgb, ${stateColor} 10%, transparent)`,
            }}
          >
            {isRunning && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: stateColor,
                  animation: "live-pulse 1.4s infinite",
                  flexShrink: 0,
                }}
              />
            )}
            {eng.state}
          </span>
        </div>

        {/* ── active call (running + active_call_id) ── */}
        {hasActiveCall && (
          <div
            style={{
              margin: "0",
              padding: "14px 24px",
              background: "color-mix(in srgb, var(--accent) 8%, var(--bg-card))",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <div>
                <div
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: 14,
                    fontWeight: 500,
                    color: "var(--text-primary)",
                  }}
                >
                  Active Call
                </div>
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--text-muted)",
                    letterSpacing: "0.04em",
                    marginTop: 2,
                  }}
                >
                  {eng.active_call_id}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  variant="secondary"
                  onClick={handleListenIn}
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 11,
                    padding: "6px 12px",
                    border: "1px solid var(--border-strong)",
                    borderRadius: "var(--radius-input)",
                    background: "transparent",
                    color: "var(--text-secondary)",
                    fontWeight: 500,
                  }}
                >
                  Listen In
                </Button>
                <Button
                  variant="danger"
                  onClick={() => haltMut.mutate()}
                  disabled={haltMut.isPending}
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: 11,
                    padding: "6px 12px",
                    border: "1px solid var(--error)",
                    borderRadius: "var(--radius-input)",
                    background: "transparent",
                    color: "var(--error)",
                    fontWeight: 500,
                    opacity: haltMut.isPending ? 0.6 : 1,
                  }}
                >
                  {haltMut.isPending ? "Halting…" : "Halt"}
                </Button>
              </div>
            </div>
            {/* CSS waveform — matches mockup .waveform bars */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                height: 28,
                marginBottom: 0,
              }}
            >
              {waveHeights.map((h, i) => (
                <span
                  key={i}
                  style={{
                    width: 3,
                    height: `${h}%`,
                    background: "var(--accent)",
                    animation: `wave 1.2s ease-in-out infinite`,
                    animationDelay: `${waveDelays[i]}s`,
                    display: "block",
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* ── schedule section ── */}
        <PanelSection title="Schedule">
          <div className="grid-2col-responsive" style={{ gap: 14 }}>
            <div>
              <span style={fieldLabelStyle}>Scheduled Start</span>
              <input
                type="datetime-local"
                value={schedStart}
                onChange={(e) => setSchedStart(e.target.value)}
                disabled={!editable}
                style={inputStyle}
              />
            </div>
            <div>
              <span style={fieldLabelStyle}>Scheduled End</span>
              <input
                type="datetime-local"
                value={schedEnd}
                onChange={(e) => setSchedEnd(e.target.value)}
                disabled={!editable}
                style={inputStyle}
              />
            </div>
          </div>
        </PanelSection>

        {/* ── active hours section ── */}
        <PanelSection title="Active Hours · within campaign window">
          <div className="grid-2col-responsive" style={{ gap: 14 }}>
            <div>
              <span style={fieldLabelStyle}>Daily Start</span>
              <input
                type="time"
                value={dailyStart}
                onChange={(e) => setDailyStart(e.target.value)}
                disabled={!editable}
                style={inputStyle}
              />
            </div>
            <div>
              <span style={fieldLabelStyle}>Daily End</span>
              <input
                type="time"
                value={dailyEnd}
                onChange={(e) => setDailyEnd(e.target.value)}
                disabled={!editable}
                style={inputStyle}
              />
            </div>
          </div>
          {/* daypart visual */}
          <div
            style={{
              marginTop: 12,
              padding: "8px 10px",
              background: "var(--bg-input)",
              border: "1px solid var(--border)",
              borderRadius: "var(--radius-input)",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                color: "var(--text-muted)",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              Daypart
            </span>
            <div style={{ flex: 1, height: 6, border: "1px solid var(--border-strong)", display: "flex", overflow: "hidden", borderRadius: 2 }}>
              <div style={{ flex: 8, background: "repeating-linear-gradient(135deg, rgba(15,23,42,0.1) 0 2px, transparent 2px 5px)" }} />
              <div style={{ flex: 10, background: "var(--accent)" }} />
              <div style={{ flex: 6, background: "repeating-linear-gradient(135deg, rgba(15,23,42,0.1) 0 2px, transparent 2px 5px)" }} />
            </div>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--text-muted)",
                letterSpacing: "0.04em",
                whiteSpace: "nowrap",
              }}
            >
              {dailyStart} → {dailyEnd}
            </span>
          </div>
        </PanelSection>

        {/* ── stagger section ── */}
        <PanelSection title="Stagger Strategy">
          <div className="grid-2col-responsive" style={{ gap: 14 }}>
            <div>
              <span style={fieldLabelStyle}>Dispatch Pattern</span>
              <select
                value={staggerPattern}
                onChange={(e) => setStaggerPattern(e.target.value)}
                disabled={!editable}
                style={selectStyle}
              >
                <option value="even_spread">Even spread</option>
                <option value="fixed_interval">Fixed interval</option>
              </select>
            </div>
            <div>
              <span style={fieldLabelStyle}>Jitter (seconds)</span>
              <input
                type="number"
                min={0}
                value={staggerJitter}
                onChange={(e) => setStaggerJitter(parseInt(e.target.value, 10) || 0)}
                disabled={!editable}
                style={inputStyle}
              />
            </div>
          </div>
          {staggerPattern === "fixed_interval" && (
            <div style={{ marginTop: 14 }}>
              <span style={fieldLabelStyle}>Interval (seconds)</span>
              <input
                type="number"
                min={1}
                value={staggerInterval}
                onChange={(e) => setStaggerInterval(parseInt(e.target.value, 10) || 60)}
                disabled={!editable}
                style={inputStyle}
              />
            </div>
          )}
        </PanelSection>

        {/* ── save button ── */}
        {editable && (
          <div style={{ padding: "14px 24px", borderBottom: "1px solid var(--border)" }}>
            <Button
              variant="primary"
              onClick={() => saveMut.mutate()}
              disabled={saveMut.isPending}
              style={{
                background: "var(--accent)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--radius-input)",
                padding: "8px 18px",
                fontSize: 13,
                fontWeight: 500,
                opacity: saveMut.isPending ? 0.7 : 1,
                fontFamily: "var(--font-sans)",
              }}
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
            {saveMut.isSuccess && (
              <span style={{ marginLeft: 12, fontSize: 12, color: "var(--success)" }}>Saved</span>
            )}
            {saveError && (
              <span style={{ marginLeft: 12, fontSize: 12, color: "var(--error)" }}>{saveError}</span>
            )}
          </div>
        )}

        {/* ── targets section ── */}
        <PanelSection title="Targets · This Engagement">
          {targets.length === 0 ? (
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>No targets assigned yet.</p>
          ) : (
            <ScrollableTable minWidth={360}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--border)" }}>
                  {["#", "Name", "Status", "Duration"].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        fontFamily: "var(--font-mono)",
                        fontSize: 9.5,
                        textTransform: "uppercase",
                        letterSpacing: "0.14em",
                        color: "var(--text-muted)",
                        padding: "7px 10px",
                        fontWeight: 500,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {targets.map((t) => (
                  <tr
                    key={t.target_sort_order}
                    style={{
                      borderBottom: "1px solid var(--border-subtle)",
                      background: t.status === "in_progress" ? "color-mix(in srgb, var(--accent) 5%, transparent)" : "transparent",
                    }}
                  >
                    <td style={{ padding: "7px 10px", color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: 11 }}>
                      {String(t.target_sort_order + 1).padStart(2, "0")}
                    </td>
                    <td style={{ padding: "7px 10px", fontWeight: 500, color: "var(--text-primary)" }}>
                      {t.target_display_name}
                    </td>
                    <td style={{ padding: "7px 10px" }}>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 9.5,
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: statusColor(t.status),
                        }}
                      >
                        {t.status.replace("_", " ")}
                      </span>
                    </td>
                    <td style={{ padding: "7px 10px", fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-muted)" }}>
                      {t.call_duration_seconds != null ? `${t.call_duration_seconds}s` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </ScrollableTable>
          )}
        </PanelSection>
      </div>
    </>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function PanelSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: "18px 24px", borderBottom: "1px solid var(--border-subtle)" }}>
      <h4
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          textTransform: "uppercase",
          letterSpacing: "0.18em",
          color: "var(--text-muted)",
          margin: "0 0 12px",
          fontWeight: 500,
        }}
      >
        {title}
      </h4>
      {children}
    </div>
  );
}

function statusColor(status: string): string {
  switch (status) {
    case "completed": return "var(--success)";
    case "failed":
    case "rejected": return "var(--error)";
    case "in_progress": return "var(--accent)";
    case "voicemail":
    case "no_answer": return "var(--text-muted)";
    default: return "var(--text-secondary)";
  }
}
